import React, { useCallback, useEffect, useRef, useState } from 'react';
import ErrorBoundary from '../components/ErrorBoundary';
import reconciliationAPI from '../features/reconciliation/services/reconciliationApi';
import MonthlyStatusGrid from '../features/reconciliation/MonthlyStatusGrid';
import PeriodPanel from '../features/reconciliation/PeriodPanel';
import StartingBalanceForm from '../features/reconciliation/StartingBalanceForm';
import StatementPanel from '../features/reconciliation/StatementPanel';
import BooksClosePanel from '../features/reconciliation/BooksClosePanel';
import { formatApiError } from '../utils/errorMessage';
import { thisYear, yearFromSearch, writeYearToUrl, yearOptions } from '../features/reconciliation/yearNav';

// Monthly bank reconciliation: status grid by account and month, then one
// month at a time (statement balances, tick cleared rows, mark reconciled).
// The open month only counts while it belongs to the year the grid shows.
export const selectionInYear = (selected, year) => (selected && String(selected.month || '').slice(0, 4) === String(year) ? selected : null);

export default function Reconciliation({ selectedBusiness }) {
  const [closedThrough, setClosedThrough] = useState(null);
  const [year, setYear] = useState(() => yearFromSearch(typeof window !== 'undefined' ? window.location.search : ''));
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState('month'); // 'month' or 'statement'
  const [statementAccount, setStatementAccount] = useState('');
  const [error, setError] = useState('');
  const reqId = useRef(0);

  const load = useCallback(async () => {
    const mine = ++reqId.current;
    try {
      const params = { year, ...(selectedBusiness ? { business_id: selectedBusiness } : {}) };
      const res = await reconciliationAPI.monthlyStatus(params);
      if (mine !== reqId.current) return; // a newer year/business load superseded this one
      setAccounts(res.data.accounts || []);
      setError('');
    } catch (err) {
      if (mine !== reqId.current) return;
      setError(formatApiError(err, 'Could not load reconciliation status'));
    }
  }, [year, selectedBusiness]);

  useEffect(() => { load(); }, [load]);
  // Keeps the year in the address bar so a refresh stays on it.
  useEffect(() => { writeYearToUrl(year); }, [year]);
  // Changing year or business closes the open month so panel and grid never disagree.
  useEffect(() => { setSelected(null); }, [year, selectedBusiness]);
  const current = selectionInYear(selected, year);
  const account = current && accounts.find((a) => a.account_id === current.accountId);
  const stmtAccount = accounts.find((a) => String(a.account_id) === String(statementAccount));

  return (
    <ErrorBoundary section="Reconciliation">
      <div className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Monthly reconciliation</h2>
            <p className="text-gray-600 text-sm">Match each account to its bank or card statement, month by month.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Choose year">
            <button type="button" className="px-3 py-2 border rounded" onClick={() => setYear(year - 1)}>Previous</button>
            <button type="button" className="px-3 py-2 border rounded" onClick={() => setYear(thisYear())} disabled={year === thisYear()}>This year</button>
            <button type="button" className="px-3 py-2 border rounded" onClick={() => setYear(Math.min(year + 1, thisYear()))} disabled={year >= thisYear()}>Next</button>
            <select aria-label="Year" className="px-2 py-2 border rounded font-semibold" value={year} onChange={(e) => setYear(Number(e.target.value))}>
              {yearOptions(accounts, year).map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        {error && <p className="text-red-600" role="alert">{error}</p>}
        <div className="flex flex-wrap items-center gap-2 text-sm" role="group" aria-label="Reconcile by">
          <span>Reconcile by</span>
          <button type="button" aria-pressed={mode === 'month'} className={`px-3 py-1 border rounded ${mode === 'month' ? 'bg-blue-600 text-white' : ''}`} onClick={() => setMode('month')}>Month</button>
          <button type="button" aria-pressed={mode === 'statement'} className={`px-3 py-1 border rounded ${mode === 'statement' ? 'bg-blue-600 text-white' : ''}`} onClick={() => setMode('statement')}>Statement</button>
          {mode === 'statement' && (
            <select aria-label="Account" className="px-2 py-1 border rounded" value={statementAccount} onChange={(e) => setStatementAccount(e.target.value)}>
              <option value="">Choose an account</option>
              {accounts.map((a) => <option key={a.account_id} value={a.account_id}>{a.account_name}</option>)}
            </select>
          )}
        </div>
        {mode === 'statement' && stmtAccount && <StatementPanel key={`st-${stmtAccount.account_id}`} account={stmtAccount} onChanged={load} />}
        {selectedBusiness ? <BooksClosePanel key={`books-${selectedBusiness}`} businessId={selectedBusiness} onClosedChange={setClosedThrough} /> : null}
        <MonthlyStatusGrid accounts={accounts} selected={current} closedThrough={selectedBusiness ? closedThrough : null} onOpen={(a, month) => setSelected({ accountId: a.account_id, month })} />
        {mode === 'month' && account && (
          <div className="bg-gray-50 rounded p-3">
            <StartingBalanceForm key={`sb-${account.account_id}`} account={account} onSaved={load} />
          </div>
        )}
        {mode === 'month' && account && <PeriodPanel key={`${account.account_id}-${selected.month}`} account={account} month={selected.month} onChanged={load} />}
      </div>
    </ErrorBoundary>
  );
}
